import React from 'react';
import { cn } from '../cn';
import { IPolymorphicBaseButtonProps, PolymorphicBaseButton } from './PolymorphicBaseButton';

type VariantNames = 'primary' | 'secondary' | 'text' | 'link';
export interface IButtonProps extends IPolymorphicBaseButtonProps {
  /**
   * Sets the button style variant.
   */
  variant?: VariantNames;
  /**
   * Applies padding to create different size buttons.
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Sets `width: 100%` when true, and `width: auto` when false or when prop is not applied.
   */
  fullWidth?: boolean;
  /**
   * Applies an icon component.
   */
  icon?: React.ReactNode;
  /**
   * Applies rounded corners to the button.
   */
  rounded?: boolean;
  /**
   * Disables interaction with the button element.
   */
  isDisabled?: boolean;
  /**
   * Applies error color styles to button.
   */
  destructive?: boolean;
  /**
   * Applies accent color styles to button.
   */
  accent?: boolean;
  /**
   * Applies white color styles to button.
   */
  white?: boolean;
  /**
   * Include additional classnames to the root element.
   */
  className?: string;
  /**
   * Allows nesting buttons inside buttons.
   */
  divButton?: boolean;
  /**
   * Removes all styles from the button element.
   */
  unstyled?: boolean;
  /**
   * Is an anchor tag.
   */
  to?: string;
}

type Variant = {
  regular: string[],
  accent: string[],
  white: string[],
  destructive: string[],
};

const variantMap: Record<VariantNames, Variant> = {
  primary: {
    regular: [`
      bg-text text-neutral-50
      hover:bg-neutral-800
      active:bg-neutral-700
      group-hover:bg-neutral-800
      group-active:bg-neutral-700
      disabled:hover:bg-neutral-900 disabled:active:bg-neutral-900
    `],
    accent: [`
      bg-accent dark
      hover:bg-accent-hover
      active:bg-accent-active
      group-hover:bg-accent-hover
      group-active:bg-accent-active
    `],
    white: [`
      bg-white text-text light
      hover:bg-surface-darker hover:text-text-light hover:border-text-lightest
      active:bg-surface-darker/90 active:text-text-lighter active:border-text
      group-hover:bg-surface-darker group-hover:text-text-lighter group-hover:border-text-lightest
      group-active:bg-surface-darker/90 group-active:text-text-light group-active:border-text
      disabled:hover:bg-surface-darker/80 disabled:hover:text-text-lighter disabled:hover:border-text
    `],
    destructive: [`
      bg-error
      hover:bg-error-hover
      active:bg-error-active
      group-hover:bg-error-hover
      group-active::bg-error-active
    `],
  },
  secondary: {
    regular: [`
      border-text text-text
      hover:bg-surface-darker/30 hover:text-text-light hover:border-text-lightest
      active:bg-surface-darker/50 active:text-text-lighter active:border-text
      group-hover:bg-surface-darker/30 group-hover:text-text-lighter group-hover:border-text-lightest
      group-active:bg-surface-darker/50 group-active:text-text-light group-active:border-text
      disabled:hover:bg-surface-darker/30 disabled:hover:text-text-lighter disabled:hover:border-text
    `],
    accent: [`
      text-accent border-accent dark
      hover:text-accent-hover hover:border-accent-hover hover:bg-[rgba(240,188,40,0.4)]  
    `],
    white: [`
      text-neutral-50 border-neutral-50 light
      hover:text-neutral-200 hover:border-neutral-200 hover:bg-[rgba(250,250,250,0.1)]  
    `],
    destructive: [`
      group-hover:text-error-hover
      hover:bg-error-lightest hover:text-error-hover hover:border-error-hover
      active:border-error-active active:bg-error-lighter active:text-error-active
      group-hover:border-error-hover group-hover:bg-error-lightest border-error text-error
      group-active:border-error-active group-active:bg-error-lighter group-active:text-error-active
    `],
  },
  text: {
    regular: [`
      inline
      bg-transparent underline border-0 text-text font-extrabold
      hover:text-text-light
      active:text-text-lighter
      group-hover:text-text-light
      group-active:text-text-lighter
      disabled:bg-transparent
    `],
    accent: [],
    white: [`
      inline
      text-text-contrast-light
      bg-transparent underline border-0 font-extrabold
      hover:text-neutral-200
      active:text-neutral-200
      group-hover:text-neutral-200
      group-active:text-neutral-200
      disabled:bg-transparent
    `],
    destructive: [`
      text-error
      hover:text-error-hover
      active:text-error-active
      group-hover:text-error-hover
      group-active:text-error-active
    `],
  },
  link: {
    regular: [`
      inline
      bg-transparent border-transparent text-primary underline-offset-4 no-underline
      hover:bg-transparent hover:border-transparent hover:text-primary-hover hover:underline 
      active:bg-transparent active:border-transparent active:text-primary-active active:underline
      group-hover:border-transparent group-hover:text-primary-hover group-hover:underline
      group-active:border-transparent group-active:text-primary-active
      disabled:bg-transparent
    `],
    accent: [`
      text-accent
      hover:text-accent-hover
      active:text-accent-active
      group-hover:text-accent-hover
      group-active:text-accent-active
    `],
    destructive: [`
      text-error
      hover:text-error-hover
      active:text-error-active
      group-hover:text-error-hover
      group-active:text-error-active
    `],
  },
};

export const Button = React.forwardRef<any, IButtonProps>(({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  rounded = false,
  destructive = false,
  className,
  divButton,
  accent,
  white,
  unstyled,
  to,
  ...props
}, ref) => {
  let Tag: any = PolymorphicBaseButton;

  const isDisabled = props.isDisabled;
  if(divButton) {
    Tag = 'div';
    delete props.isDisabled; // Prevent React warning. We manage this with state so the warning doesn't matter for div buttons.
  }

  return (
    <Tag
      className={cn(
        accent ? 'light' : '',
        'group/button cursor-pointer disabled:cursor-not-allowed',
        'focus:outline-2 focus:outline-focus focus:outline-offset-2 focus:rounded-sm',
        !unstyled && [
          'font-medium',
          'disabled:opacity-50',
          'flex flex-row gap-2 items-center border-1',
          'transition-colors duration-100 ease-out group-hover:ease-in hover:ease-in group-hover:duration-hover hover:duration-hover',
          size === 'sm' ? ['py-[5px] px-[9px]'] : size === 'md' ? ['py-[7px]  px-[15px]'] : size === 'lg' ? ['py-[9px] px-[18px]'] : [],
          rounded ? ['rounded-xl'] : [''],
          fullWidth ? ['w-full'] : ['w-auto'],
          (variant !== 'link' && variant !== 'text') && 'border-transparent text-[16px] leading-[16px] font-text' || 'border-0',
          variantMap[variant]?.regular,
          accent && variantMap[variant]?.accent,
          white && variantMap[variant]?.white,
          destructive && variantMap[variant]?.destructive,
          Tag == 'div' && isDisabled && 'cursor-not-allowed opacity-50',
        ],
        className
      )}
      ref = {ref}
      to = {isDisabled ? '' : to /* If we want to disable an anchor we must remove the href */ }
      {...props}
    >
      {icon && icon}{props.children}
    </Tag>
  );
});
